// =============================================================
// mailSync.js — pure helpers for the Mail page's fast load and delta sync.
//
//   applyMailDelta   — fold a `GET /api/mail/sync` change list into the list
//                      on screen, honouring the filters that list was loaded
//                      under (unread only, flagged only, Focused/Other).
//   syncSinceFor     — how far back to seed the delta: the oldest message the
//                      page loaded, clamped to the server's window.
//   canDeltaSync     — whether the current view can be kept current by delta
//                      at all (a search result or a non-date sort cannot).
//   formatTiming     — one console line comparing the browser's round trip
//                      with the server's own timing block.
//   mailViewCache    — an IN-MEMORY cache of the last folders and list per
//                      mailbox, so returning to Mail within the app paints at
//                      once. Memory only, deliberately: message subjects and
//                      previews are not written to browser storage, so a
//                      shared machine keeps nothing after the tab closes.
// =============================================================

export const SYNC_WINDOW_DAYS = 60;
const DAY_MS = 86400000;

function matchesView(m, { unreadOnly, flaggedOnly, focus } = {}) {
  if (unreadOnly && m.isRead) return false;
  if (flaggedOnly && !m.flagged) return false;
  if (focus && focus !== 'all' && m.focus && m.focus !== focus) return false;
  return true;
}

const byNewest = (a, b) => String(b.receivedAt || '').localeCompare(String(a.receivedAt || ''));

/**
 * Apply delta changes to the list on screen.
 *
 * `keepIds` are messages that must not vanish even if they stop matching the
 * filter — the one the person is reading (opening it marks it read, and an
 * unread-only list dropping it out from under them reads as a bug).
 *
 * A NEW message is added only when it falls inside the window already loaded
 * (on or after the oldest row on screen, unless the list is shorter than a
 * page) — inserting one older than everything shown would put a hole in the
 * list's chronology that "Load more" would then duplicate.
 */
export function applyMailDelta(messages, changes, { unreadOnly = false, flaggedOnly = false, focus = 'all', keepIds = [], windowComplete = false } = {}) {
  const list = Array.isArray(messages) ? messages : [];
  if (!Array.isArray(changes) || !changes.length) return { messages: list, added: 0, updated: 0, removed: 0 };
  const keep = new Set(keepIds.filter(Boolean));
  const byId = new Map(list.map((m) => [m.id, m]));
  const oldest = list.length ? list.reduce((o, m) => (String(m.receivedAt || '') < o ? String(m.receivedAt || '') : o), String(list[0].receivedAt || '')) : '';
  const view = { unreadOnly, flaggedOnly, focus };
  let added = 0; let updated = 0; let removed = 0;

  for (const c of changes) {
    if (!c || !c.id) continue;
    if (c.removed) {
      if (byId.delete(c.id)) removed += 1;
      continue;
    }
    const prev = byId.get(c.id);
    const next = prev ? { ...prev, ...(c.patch || {}) } : (c.item || null);
    if (!next) continue;
    if (!matchesView(next, view) && !keep.has(c.id)) {
      if (prev) { byId.delete(c.id); removed += 1; }
      continue;
    }
    if (!prev) {
      if (!windowComplete && oldest && String(next.receivedAt || '') < oldest) continue;
      added += 1;
    } else {
      updated += 1;
    }
    byId.set(c.id, next);
  }
  return { messages: [...byId.values()].sort(byNewest), added, updated, removed };
}

/** The seed's `since`: the oldest loaded message, no older than the window; a week when nothing is loaded. */
export function syncSinceFor(messages, now = Date.now()) {
  const floor = now - SYNC_WINDOW_DAYS * DAY_MS;
  let oldest = null;
  for (const m of messages || []) {
    const t = Date.parse(m && m.receivedAt);
    if (Number.isFinite(t) && (oldest === null || t < oldest)) oldest = t;
  }
  const t = oldest === null ? now - 7 * DAY_MS : Math.max(oldest, floor);
  return new Date(t).toISOString();
}

/** Delta can keep a plain date-ordered folder list current; not a search, another sort, or all-folder scope. */
export function canDeltaSync({ search, sort, sortDir, searchAll, folderId }) {
  if (!folderId || String(folderId).startsWith('__')) return false;
  if (String(search || '').trim()) return false;
  if (searchAll) return false;
  return (sort || 'date') === 'date' && (sortDir || 'desc') === 'desc';
}

/** One console line: browser round trip vs the server's own breakdown. */
export function formatTiming(label, clientMs, t) {
  if (!t) return `[mail] ${label} ${clientMs}ms (no server timing)`;
  const tok = t.token ? `token ${t.token.source || 'none'} ${t.token.tokenMs || 0}ms` : 'token n/a';
  const slow = (t.slowest || []).map((s) => `${s.path} ${s.ms}ms`).join(', ');
  const extra = [];
  if (t.folderSource) extra.push(`folders via ${t.folderSource} (${t.folderCount})`);
  if (t.wellKnownCached != null) extra.push(`well-known ${t.wellKnownCached ? 'cached' : 'looked up'}`);
  if (t.changes != null) extra.push(`${t.changes} changes${t.seeded ? ' (seed)' : ''}`);
  const overhead = Math.max(0, clientMs - (t.totalMs || 0));
  return `[mail] ${label} ${clientMs}ms in browser · server ${t.totalMs}ms`
    + ` (${tok}; graph ${t.graphCalls} calls ${t.graphMs}ms${extra.length ? `; ${extra.join('; ')}` : ''})`
    + ` · network+host ${overhead}ms${slow ? ` · slowest: ${slow}` : ''}`;
}

// ── In-memory view cache ───────────────────────────────────────────────────

const MAX_CACHED_LISTS = 20;
const _folders = new Map(); // mailboxKey → { folders, wellKnown }
const _lists = new Map();   // listKey → { messages, nextPageToken }

const mbKey = (mailbox) => (mailbox ? String(mailbox).toLowerCase() : '__me__');

export const mailViewCache = {
  getFolders(mailbox) { return _folders.get(mbKey(mailbox)) || null; },
  setFolders(mailbox, value) { _folders.set(mbKey(mailbox), value); },
  listKey(mailbox, folderId, view) {
    return [mbKey(mailbox), folderId, view.unreadOnly ? 1 : 0, view.flaggedOnly ? 1 : 0, view.focus || 'all', view.sort || 'date', view.sortDir || 'desc'].join('|');
  },
  getList(key) { return _lists.get(key) || null; },
  setList(key, value) {
    if (_lists.has(key)) _lists.delete(key);
    _lists.set(key, value);
    while (_lists.size > MAX_CACHED_LISTS) _lists.delete(_lists.keys().next().value);
  },
  clear() { _folders.clear(); _lists.clear(); },
};
